import {observable, action} from 'mobx';
import FormStore from "./FormStore";
import api from "./api";
import React from "react";
import * as yup from "yup";
import {ValidationError} from "yup";
import {toast} from "react-toastify";


class ClientStore {
  constructor(api) {
    this.api = api;
    this.settingsForm = new FormStore(this);
    this.settingsForm.data = this.defaultFormData;

  }

  defaultFormData = {
    company: '',
    name: '',
    phone_number: '',
    email: '',
    support_email: '',
    facebook_id: '',
    twitter_id: '',
    address: {
      street: '',
      street2: '',
      city: '',
      state: '',
      zipcode: '',
    },
    timezone: ''
  }
  @observable client;
  @observable telephonies;
  @observable uploadingLogo = false;
  @observable token;
  @observable resetingToken = false;
  @observable savingInfo = false;

  phoneRegExp = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
  zipcodeRegExp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  clientSchema = yup.object({
    company: yup
        .string()
        .trim()
        .required('Company is required'),
    name: yup
        .string()
        .trim()
        .required('Name is required'),
    phone_number: yup
        .string()
        .trim()
        .matches(this.phoneRegExp, { message: 'Invalid format. Please use one of the following formats +1000000000, (000) 000-0000, 000-000-0000', excludeEmptyString: true }),
    email: yup
        .string()
        .trim()
        .email('Invalid email format'),
    support_email: yup
        .string()
        .trim()
        .email('Invalid email format'),
    address: yup.object({
      street: yup
          .string()
          .trim(),
      street2: yup
          .string()
          .trim(),
      city: yup
          .string()
          .trim(),
      state: yup
          .string()
          .trim(),
      zipcode: yup
          .string()
          .trim()
          .matches(this.zipcodeRegExp, { message: 'Invalid format.  Please use the format 00000 or 00000-0000', excludeEmptyString: true }),
    }).test('validAddress',
        'Address is invalid',
        function (item,testContext) {
          const keys = ['street', 'street2', 'city', 'state', 'zipcode']
          const mappedKeys = new Map();
          mappedKeys.set('street', 'Address Line 1');
          mappedKeys.set('street2', 'Address Line 2');
          mappedKeys.set('city', 'City');
          mappedKeys.set('state', 'State');
          mappedKeys.set('zipcode', 'Zipcode');


            let errors = [];

            if(testContext?.parent?.insurance_enabled) {
                errors = keys.filter(k => k !== 'street2').map((key) => {
                  const value = item[key];
                if (item[key] ) {
                  return null
                } // Successful validation, no error

                let mappedKey = mappedKeys.get(key);
                if(mappedKey === undefined) {
                    mappedKey = key;
                }
                return new ValidationError(
                    `${mappedKey} is required when Opt-in for Insurance is enabled`,
                    value,
                    `address.${key}`
                )
              }).filter(Boolean)
            }else {
              const addressElementFound = keys.map((key) => {
                const value = item[key];
                if (value) {
                  return true;
                }
              }).filter(Boolean);
              if (addressElementFound.length > 0) {
                errors = keys.filter(k => k !== 'street2').map((key) => {
                  const value = item[key];
                  if (value) {
                    // Successful validation, no error
                    return null
                  }
                  let mappedKey = mappedKeys.get(key);
                  if(mappedKey === undefined) {
                    mappedKey = key;
                  }
                  return new ValidationError(
                      `${mappedKey} is required when other address fields are filled in`,
                      value,
                      `address.${key}`
                  )
                }).filter(Boolean);
              }
            }


          if (errors.length === 0) { return true }

          return testContext.createError({
            message: () => errors
          })
        })

  });

  @action
  async get() {
    const response = await this.api.get('/me');
    if(response.status === 200) {
      const client =  response.data.client;
      const settingsResponse = await this.api.get("/shipments/client-settings")
      if (settingsResponse.status === 200) {
        client['insurance_enabled'] = settingsResponse.data?.client_settings?.settings?.insurance_enabled?.toLowerCase?.() === 'true';
      }
      this.settingsForm.data = { ...this.defaultFormData, ...client};
    }
  }

  @action
  getTelephonies() {
    this.api.get('/clients/telephonies').then(response => {
      if (response.status === 200) {
        this.telephonies = response.data;
      }
    })
  }

  @action
  getToken() {
    this.api.get('/clients/token').then(response => {
      if (response.status === 200) {
        this.token = response.data;
      }
    })
  }

  @action
  resetToken() {
    this.resetingToken = true;
    this.api.put('/clients/token').then(response => {
      this.resetingToken = false;
      if (response.status === 200) {
        this.token = response.data;
      }
    })
  }

  @action
  updateInfo() {
    this.savingInfo = true;

    //save this, so we don't need to make another api call to get this value (it is read only)
    const originalInsuranceEnabledValue = this.settingsForm.data.insurance_enabled;
    const originalEmailValue = this.settingsForm.data.email;

    const clientUpdateRequest = { ...this.settingsForm.data };
    delete clientUpdateRequest.insurance_enabled;
    delete clientUpdateRequest.email;

    this.api.put('/clients/me', clientUpdateRequest).then(response => {
      if (response.status === 200) {
        this.settingsForm.data = {
          ...this.defaultFormData,
          ...response.data,
          insurance_enabled: originalInsuranceEnabledValue,
        };
        toast.success('Client info updated successfully');
      }else if(response.data.errors){
        toast.error(response.data.errors[0]);
      }else{
        toast.error('Update failed')
      }
    }).finally(() => {
        this.savingInfo = false;
    })
  }

  @action
  uploadPhoto(file, cb) {
    const formData = new FormData();
    formData.append('logo', file);
    const config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    };

    this.uploadingLogo = true;
    api.put(`/clients/logo`, formData, config)
      .then(response => {
        this.uploadingLogo = false;
        if (response.status === 200) {
          this.settingsForm.data.logo_url = response.data;
          cb(response.data);
        }
      });
  }

  @action
  validateClientInfo() {
    return this.settingsForm.isValid(this.clientSchema)
  }
}

export default ClientStore;
